/**
 * Räknar hur många SMS ett meddelande blir. Avsändaren betalar per segment och mottagare, så
 * ett utskick till hela kåren blir dyrt fort — vyn visar antalet innan utskicket.
 *
 * Reglerna är GSM 03.38: ryms hela texten i GSM-7 går 160 tecken i ett segment och 153 per
 * segment när meddelandet delas upp. Ett enda tecken utanför teckenuppsättningen (t.ex. en
 * emoji eller ett typografiskt citattecken) tvingar hela meddelandet till UCS-2, där gränserna
 * är 70 respektive 67. Tecknen i GSM-7:ans utökade tabell ({ } [ ] ~ ^ | \ € ) tar två platser.
 */

const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

const GSM7_EXTENDED = '^{}\\[~]|€';

const GSM7_BASIC_SET = new Set(GSM7_BASIC.split(''));
const GSM7_EXTENDED_SET = new Set(GSM7_EXTENDED.split(''));

export type SmsEncoding = 'GSM-7' | 'UCS-2';

export interface SmsLength {
  encoding: SmsEncoding;
  /** Antal teckenplatser meddelandet tar (utökade GSM-tecken räknas som två). */
  units: number;
  /** Antal SMS-segment meddelandet skickas som. Tomt meddelande ger 0. */
  segments: number;
  /** Teckenplatser kvar innan meddelandet växer till ytterligare ett segment. */
  remainingInSegment: number;
}

const segmentSizes = (encoding: SmsEncoding) =>
  encoding === 'GSM-7'
    ? { single: 160, multi: 153 }
    : { single: 70, multi: 67 };

export const measureSms = (text: string): SmsLength => {
  // Surrogatpar (emoji) måste räknas som två UCS-2-enheter, därför split('') och inte [...text].
  const chars = text.split('');

  const isGsm7 = chars.every(
    (c) => GSM7_BASIC_SET.has(c) || GSM7_EXTENDED_SET.has(c)
  );
  const encoding: SmsEncoding = isGsm7 ? 'GSM-7' : 'UCS-2';

  const units = isGsm7
    ? chars.reduce((sum, c) => sum + (GSM7_EXTENDED_SET.has(c) ? 2 : 1), 0)
    : chars.length;

  const { single, multi } = segmentSizes(encoding);

  if (units === 0) {
    return { encoding, units: 0, segments: 0, remainingInSegment: single };
  }

  const segments = units <= single ? 1 : Math.ceil(units / multi);
  const capacity = segments === 1 ? single : segments * multi;

  return {
    encoding,
    units,
    segments,
    remainingInSegment: capacity - units,
  };
};
